import { type FC } from 'react'

import { useExperimentalAddStaticFileHost } from '../../../../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import { useGlobalConfigContext } from '../../../../../../global-config'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC,
  imageAccessorIsImageHostedOnCdn,
  NextImageWithFallback,
  Picture,
} from '../../../../../../image'
import { productAccessorIsPrescriptionProduct } from '../../../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { useProductPageContext } from '../../../../productPageContext'

import { type ProductCarouselGalleryImageProps } from './ProductCarouselGalleryImageProps'

const IMAGE_SIZES_RELATIVE_TO_VIEWPORT_WIDTH: [number, number][] = [
  /* eslint-disable @typescript-eslint/no-magic-numbers -- the rule was disabled before a comment became required */
  [1000, 400],
  [920, 320],
  [770, 256],
  [570, 768],
  [520, 425],
  [470, 375],
  /* eslint-enable @typescript-eslint/no-magic-numbers */
]

const IMAGE_LARGE_SIZES_RELATIVE_TO_VIEWPORT_WIDTH: [number, number][] = [
  /* eslint-disable @typescript-eslint/no-magic-numbers -- the rule was disabled before a comment became required */
  [770, 650],
  /* eslint-enable @typescript-eslint/no-magic-numbers */
]
const SMALLEST_IMAGE_SIZE = 320

const ProductCarouselGalleryImage: FC<ProductCarouselGalleryImageProps> = ({
  alt,
  lazy = false,
  source,
  useLargeSizes = false,
}) => {
  const { product } = useProductPageContext()

  const { cdn } = useGlobalConfigContext()
  const isCdnImage = imageAccessorIsImageHostedOnCdn(source, cdn.url)
  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()
  const fallbackImageUrl = productAccessorIsPrescriptionProduct(product)
    ? addStaticCdnHostToUrl(IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC)
    : addStaticCdnHostToUrl(IMAGE_PRODUCT_FALLBACK_SRC)

  return isCdnImage ? (
    <Picture
      alt={alt}
      className="block h-full"
      imageSizes={
        useLargeSizes
          ? IMAGE_LARGE_SIZES_RELATIVE_TO_VIEWPORT_WIDTH
          : IMAGE_SIZES_RELATIVE_TO_VIEWPORT_WIDTH
      }
      lazy={lazy}
      smallestImageSize={SMALLEST_IMAGE_SIZE}
      src={source}
    />
  ) : (
    <NextImageWithFallback
      alt={alt}
      className="object-contain"
      fallbackSrc={fallbackImageUrl}
      fill
      loading={lazy ? 'lazy' : 'eager'}
      src={source}
      unoptimized
    />
  )
}

export { ProductCarouselGalleryImage }
